import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdQcTemplatesIdBody,
  PostAdminV1StoresStoreIdQcTemplatesBody,
  QcTemplate,
} from '@nucleus-commerce/api-client/admin';

import { qcTemplatesApi } from '../api/workflows-api';

import { workflowKeys } from './workflow-keys';

type QueryClient = ReturnType<typeof useQueryClient>;

function remember(queryClient: QueryClient, checklist: QcTemplate) {
  queryClient.setQueryData(workflowKeys.qcDetail(checklist.storeId, checklist.id), checklist);
  return queryClient.invalidateQueries({ queryKey: workflowKeys.qcList(checklist.storeId) });
}

export function useCreateQcTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdQcTemplatesBody) =>
      qcTemplatesApi.create(storeId, body),
    meta: { successMessage: 'workflows:qc.toasts.created', silentError: true },
    onSuccess: (checklist) => remember(queryClient, checklist),
  });
}

export function useUpdateQcTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdQcTemplatesIdBody }) =>
      qcTemplatesApi.update(storeId, id, body),
    meta: { successMessage: 'workflows:qc.toasts.updated', silentError: true },
    onSuccess: (checklist) => remember(queryClient, checklist),
  });
}

/** 409 RESOURCE_IN_USE while a workflow template's QC stage points at it. */
export function useDeleteQcTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qcTemplatesApi.remove(storeId, id),
    meta: { successMessage: 'workflows:qc.toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: workflowKeys.qcDetail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: workflowKeys.qcList(storeId) });
    },
  });
}
